import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Menu, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faChevronDown,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer } from "react-toastify";
import { toastify } from "../../../components/Toast";
import DismissButton from "../../../assets/icons/dismissButton.svg";
import EmailGrey from "../../../assets/icons/emailGrey.svg";
import "./KindleList.scss";

const AMAZON_NOTEBOOK_URL = "https://read.amazon.com/kp/notebook?myhighlight";

const classNames = (...classes) => classes.filter(Boolean).join(" ");

const KindleList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kindles, setKindles] = useState({});
  const [cardFilterDefault, setCardFilterDefault] = useState("Most_Recent");
  const [cardLabelDefault, setLabelFilterDefault] = useState("Most Recent");
  const [invitePopup, setInvitePopup] = useState(false);
  const [stopSyncPopup, setStopSyncPopup] = useState(false);
  const [syncPopup, setSyncPopup] = useState(false);
  const [kindleAutoSync, setKindleAutoSync] = useState(false);
  const [refreshBooks, setRefreshBooks] = useState(false);
  const [refreshAutoSync, setRefreshAutoSync] = useState(false);

  const books = kindles?.bookList || [];
  const focusedBooks = books.filter((book) => book.isFocused);

  const requestBooksRefresh = () => setRefreshBooks((current) => !current);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchBooks = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}books/list?filterValue=${cardFilterDefault}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setKindles(response?.data?.data || {});
      } catch (error) {
        console.error("Error fetching Kindle books:", error);
        toastify("error", error?.response?.data?.message || "Unable to load Kindle books");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBooks();
  }, [cardFilterDefault, refreshBooks]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchKindleStatus = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setKindleAutoSync(Boolean(response?.data?.data?.kindleAutoSync));
      } catch (error) {
        console.error("Error fetching Kindle sync status:", error);
      }
    };

    fetchKindleStatus();
  }, [refreshAutoSync]);

  const filterDropDown = ["Most Recent", "Most Highlights", "Book Title", "Author Name"];
  const accountActions = [kindleAutoSync ? "Stop Sync" : "Start Sync", "Delete all"];

  const handleFilterText = (text) => {
    const filterValues = {
      "Most Recent": "Most_Recent",
      "Most Highlights": "Most_Highlights",
      "Book Title": "Book_Title",
      "Author Name": "Author_Name",
    };

    setLabelFilterDefault(text);
    setCardFilterDefault(filterValues[text]);
  };

  const handleAccountAction = (action) => {
    if (action === "Delete all") setInvitePopup(true);
    if (action === "Stop Sync" || action === "Start Sync") setStopSyncPopup(true);
  };

  const decodeHtmlEntities = (text = "") => {
    const parser = new DOMParser();
    return parser.parseFromString(text, "text/html").documentElement.textContent;
  };

  const handleFocusClick = async (event, book) => {
    event.preventDefault();
    event.stopPropagation();

    if (!book.isFocused && focusedBooks.length >= 2) {
      toastify("error", "A maximum of two daily email books is allowed");
      return;
    }

    const nextFocusState = !book.isFocused;
    setKindles((current) => ({
      ...current,
      bookList: (current?.bookList || []).map((item) =>
        item._id === book._id ? { ...item, isFocused: nextFocusState } : item
      ),
    }));

    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}books/update`,
        { bookId: book._id, isFocused: nextFocusState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
    } catch (error) {
      setKindles((current) => ({
        ...current,
        bookList: (current?.bookList || []).map((item) =>
          item._id === book._id ? { ...item, isFocused: Boolean(book.isFocused) } : item
        ),
      }));
      console.error("Error updating daily email book:", error);
      toastify("error", error?.response?.data?.message || "Unable to update this book");
    }
  };

  const updateKindleStatus = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}books/update-kindle-status`,
        { status: !kindleAutoSync },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      setRefreshAutoSync((current) => !current);
      requestBooksRefresh();
      setStopSyncPopup(false);
    } catch (error) {
      console.error("Error updating Kindle sync status:", error);
      toastify("error", error?.response?.data?.message || "Unable to update Kindle sync");
    }
  };

  const deleteAllBooks = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API}books/delete-all-books`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response.data.message);
      requestBooksRefresh();
      setInvitePopup(false);
    } catch (error) {
      console.error("Error deleting Kindle books:", error);
      toastify("error", error?.response?.data?.message || "Unable to delete Kindle books");
    }
  };

  const renderSortMenu = () => (
    <Menu as="div" className="kindle-sort-menu">
      <Menu.Button className="kindle-sort-button" aria-label="Sort Kindle highlights">
        <span>Sort By</span>
        <strong>{cardLabelDefault}</strong>
        <FontAwesomeIcon icon={faChevronDown} />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="kindle-dropdown-menu">
          {filterDropDown.map((item) => (
            <Menu.Item key={item}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => handleFilterText(item)}
                  className={classNames(active && "is-active", item === cardLabelDefault && "is-selected")}
                >
                  {item}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );

  return (
    <>
      {isLoading ? (
        <div className="App-loading"><div className="App-logo" title="Zimzee" /></div>
      ) : (
        <section className="kindleList-wholesection" aria-labelledby="kindle-highlights-title">
          <header className="kindle-page-header">
            <div className="kindle-title-row">
              <h1 id="kindle-highlights-title">Kindle Highlights</h1>
              <span className="kindle-book-count">{books.length}</span>
              <Menu as="div" className="kindle-actions-menu">
                <Menu.Button className="kindle-icon-button" aria-label="Kindle highlight actions">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Menu.Button>
                <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                  <Menu.Items className="kindle-dropdown-menu kindle-actions-dropdown">
                    {accountActions.map((action) => (
                      <Menu.Item key={action}>
                        {({ active }) => (
                          <button type="button" onClick={() => handleAccountAction(action)} className={classNames(active && "is-active", action === "Delete all" && "is-danger")}>
                            {action}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
            <div className="kindle-header-actions">
              {books.length ? (
                <button type="button" onClick={() => setSyncPopup(true)} className="kindle-sync-button">
                  <FontAwesomeIcon icon={faArrowsRotate} />
                  Sync Highlights
                </button>
              ) : (
                <a href={AMAZON_NOTEBOOK_URL} className="kindle-sync-button">
                  <FontAwesomeIcon icon={faArrowsRotate} />
                  Sync Highlights
                </a>
              )}
              {renderSortMenu()}
            </div>
          </header>

          <p className="kindle-last-synced">Last synced on {kindles?.lastKindleSync ? moment(kindles.lastKindleSync).format("MMM D, YYYY @ hh:mm A") : "---"}</p>

          {books.length ? (
            <div className="kindle-book-grid">
              {books.map((book) => {
                const title = decodeHtmlEntities(book?.title);
                const colors = book?.colors || [];
                return (
                  <article className="kindle-book-card" key={book._id}>
                    <a href={`/app/kindledetail/${book._id}`} className="kindle-book-link" aria-label={`Open ${title}`}>
                      <div className="kindle-cover-wrap">
                        <img className="kindleimg-content" src={book?.image} alt={`${title} cover`} />
                        <div className="kindle-highlight-colors" aria-hidden="true">
                          {colors.length ? colors.map((color, index) => (
                            <span key={`${color}-${index}`} style={{ backgroundColor: color.trim().toLowerCase() === "yellow" ? "#F2E366" : color }} />
                          )) : <span className="kindle-color-placeholder" />}
                        </div>
                        <span className="kindle-highlight-count">{book?.totalHighlightOfBook || 0}</span>
                        <button
                          type="button"
                          className={classNames("kindle-email-toggle", book.isFocused && "is-selected")}
                          onClick={(event) => handleFocusClick(event, book)}
                          aria-pressed={Boolean(book.isFocused)}
                          title={book.isFocused ? "Remove from daily email" : "Add to daily email"}
                          aria-label={`${book.isFocused ? "Remove" : "Add"} ${title} ${book.isFocused ? "from" : "to"} daily email`}
                        >
                          <img src={EmailGrey} alt="" />
                        </button>
                      </div>
                      <h2 title={title}>{title}</h2>
                      <p title={book?.authorName || "Unknown author"}>{book?.authorName || "Unknown author"}</p>
                    </a>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="kindle-empty-state">
              <h2>Your Kindle library is ready to connect</h2>
              <p>Sync your Kindle notebook to see books, highlights, and daily-email selections here.</p>
              <a href={AMAZON_NOTEBOOK_URL} className="kindle-sync-button"><FontAwesomeIcon icon={faArrowsRotate} />Connect Kindle</a>
            </div>
          )}

          <footer className="kindle-email-footer">
            <div className="kindle-email-status">
              <span className="kindle-email-status-icon"><img src={EmailGrey} alt="" /></span>
              <div><strong>Daily Email (click to enable, max 2 books)</strong></div>
            </div>
            <div className={classNames("kindle-rotation-status", focusedBooks.length === 2 && "is-ready")}>
              <span><img src={EmailGrey} alt="" /></span>
              <div><strong>Random rotation</strong></div>
            </div>
          </footer>
        </section>
      )}

      {invitePopup && (
        <div className="filesuploadpopup relative !z-[999]" style={{ height: "100%" }}>
          <div className="subinviteupload-section">
            <div className="teaminvitesection flex"><div className="teammembers-content">Delete all books</div><button type="button" className="dismissSubfiles-content" onClick={() => setInvitePopup(false)}><img src={DismissButton} alt="Close" /></button></div>
            <hr />
            <div className="actiontext-content">This action cannot be undone. This will delete all books and highlights. Are you sure you want to delete your books?</div>
            <div className="flex justify-end lastfile-section"><button type="button" className="cancelteam-content" onClick={() => setInvitePopup(false)}>Cancel</button><button type="button" className="buttonpadding-content text-[16px] rounded-full bg-[#FF6600] text-white" onClick={deleteAllBooks}><span className="saveteambutton-context">Yes</span></button></div>
          </div>
        </div>
      )}

      {syncPopup && (
        <div className="filesuploadpopup relative !z-[999]" style={{ height: "100%" }}>
          <div className="subinviteupload-section">
            <div className="teaminvitesection flex"><div className="teammembers-content">Sync Kindle Highlights</div><button type="button" className="dismissSubfiles-content" onClick={() => setSyncPopup(false)}><img src={DismissButton} alt="Close" /></button></div>
            <hr />
            <div className="actiontext-content"><p style={{ marginBottom: "10px" }}>Click YES to sync and update your new Kindle highlights and books manually.</p><p>After the first initial sync, you do not need to sync manually. Zimzee will automatically sync any new highlights in the background.</p></div>
            <div className="flex justify-end lastfile-section"><button type="button" className="cancelteam-content" onClick={() => setSyncPopup(false)}>Cancel</button><a className="buttonpadding-content text-[16px] rounded-full bg-[#FF6600] text-white" href={AMAZON_NOTEBOOK_URL}><span className="saveteambutton-context">Yes</span></a></div>
          </div>
        </div>
      )}

      {stopSyncPopup && (
        <div className="filesuploadpopup relative !z-[999]" style={{ height: "100%" }}>
          <div className="subinviteupload-section">
            <div className="teaminvitesection flex"><div className="teammembers-content">{kindleAutoSync ? "Stop" : "Start"} Kindle Sync</div><button type="button" className="dismissSubfiles-content" onClick={() => setStopSyncPopup(false)}><img src={DismissButton} alt="Close" /></button></div>
            <hr />
            <div className="actiontext-content"><p style={{ marginBottom: "10px" }}>This will {kindleAutoSync ? "stop" : "start"} syncing your Kindle books automatically in the background.</p><p>Click YES to confirm.</p></div>
            <div className="flex justify-end lastfile-section"><button type="button" className="cancelteam-content" onClick={() => setStopSyncPopup(false)}>Cancel</button><button type="button" className="buttonpadding-content text-[16px] rounded-full bg-[#FF6600] text-white" onClick={updateKindleStatus}><span className="saveteambutton-context">Yes</span></button></div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default KindleList;
